import { ShieldCheck } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { useClearSigning } from '../hooks/useClearSigning.js'

interface Props {
  to: string | undefined
  data: string | undefined
  value?: string
}

/**
 * Verified, protocol-authored calldata description from safeward's vendored
 * ERC-7730 descriptors (see ui/src/erc7730/). Renders nothing when the target
 * contract or selector has no matching descriptor - callers should keep
 * rendering the existing best-effort decoders (decode.ts / DecodedCalldata)
 * underneath, which stay the fallback for the ~28% of whitelisted contracts
 * without ERC-7730 coverage.
 */
export function ClearSigningPanel({ to, data, value }: Props) {
  const result = useClearSigning(to, data, value)
  if (!result) return null

  return (
    <div className="mt-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="size-3" />
          {result.contractName}
          {result.owner && <span className="opacity-70">· {result.owner}</span>}
        </div>
        <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
          Verified · ERC-7730
        </Badge>
      </div>

      <div className="font-medium" data-mask>
        {result.intent}
      </div>

      {result.fields.length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {result.fields.map((field, i) => (
            <div key={i} className="flex gap-2 text-xs">
              <span className="shrink-0 text-muted-foreground">{field.label}:</span>
              <span className="break-all font-mono" data-mask>
                {field.value}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-1 text-[10px] text-muted-foreground">
        fn: <span className="font-mono">{result.functionName}</span>
      </div>
    </div>
  )
}
